// app/customs/aiagent/index.tsx
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as Global from '@utils/global';
import * as AuthContext from '@/utils/authcontext';
import { useAgentView } from '@/utils/agentviewcontext';
import './index.css';

const moodIcons = ['😊', '🔍', '⏳', '🌱', '😮', '😅'];

type Agent = {
  エージェントGUID: string;
  所有社員GUID?: string;
  ニックネーム?: string;
  ベース人格GUID?: string;
  ベース人格名?: string;
  メモ?: string;
};

type AgentEvent = {
  seq: number;
  status?: string;
  role?: string;
  message?: string;
  display_flag?: number;
};

type AiAgentProps = {
  employeeGUID: string;
  employeeDomain: string;
};

const AiAgent: React.FC<AiAgentProps> = ({ employeeGUID, employeeDomain }) => {
  const { agentViewContext } = useAgentView();
  const { authToken } = AuthContext.useAuth();
  const [micEnabled, setMicEnabled] = useState(false);
  const [speakerEnabled, setSpeakerEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [activeAgentGUID, setActiveAgentGUID] = useState('');
  const [activityText, setActivityText] = useState('読込中');
  const [promptText, setPromptText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [activeJobGuid, setActiveJobGuid] = useState('');
  const [lastEventSeq, setLastEventSeq] = useState(0);
  const [agentMessages, setAgentMessages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const activityRef = useRef<HTMLDivElement | null>(null);
  const moodIcon = useMemo(() => moodIcons[Math.floor(Math.random() * moodIcons.length)], []);
  const viewContextLabel = useMemo(() => {
    if (!agentViewContext) return '';
    const display = agentViewContext.display || agentViewContext.spread_id || '';
    const mode = agentViewContext.mode ? `/${agentViewContext.mode}` : '';
    return display ? `${display}${mode}` : '';
  }, [agentViewContext]);
  const buildBaseInterface = () => ({
    user_guid: employeeGUID,
    user_domain: employeeDomain,
    ip_address: typeof window === 'undefined' ? '' : window.location.hostname,
    user_agent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  });

  useEffect(() => {
    const loadAgent = async () => {
      const baseInterface = buildBaseInterface();
      try {
        const listRes = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/myagentlist`, {
          base_interface: baseInterface,
          body_data: {},
        });
        const nextAgents = Array.isArray(listRes.data?.agents) ? listRes.data.agents : [];
        setAgents(nextAgents);
        const activeRes = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/get_activeagent`, {
          base_interface: baseInterface,
          body_data: {
            社員GUID: employeeGUID,
          },
        });
        const nextActiveAgentGUID = activeRes.data?.active_agent?.エージェントGUID ?? '';
        setActiveAgentGUID(nextActiveAgentGUID);
        setActivityText(nextActiveAgentGUID ? '待機中' : 'エージェント未選択');
      } catch {
        setAgents([]);
        setActiveAgentGUID('');
        setActivityText('読込失敗');
      }
    };
    loadAgent();
  }, [employeeGUID, employeeDomain]);

  const changeActiveAgent = async (agentGUID: string) => {
    const baseInterface = buildBaseInterface();
    setActiveAgentGUID(agentGUID);
    setActivityText('変更中');
    try {
      const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/set_activeagent`, {
        base_interface: baseInterface,
        body_data: {
          社員GUID: employeeGUID,
          エージェントGUID: agentGUID || null,
        },
      });
      setActiveAgentGUID(res.data?.active_agent?.エージェントGUID ?? '');
      setActivityText(agentGUID ? '待機中' : 'エージェント未選択');
    } catch {
      setActivityText('変更失敗');
    }
  };

  const sendPrompt = async () => {
    const prompt = promptText.trim();
    if (!prompt || isSending) return;
    const token = Global.getCurrentAuthToken(authToken);
    if (!token) {
      setActivityText('ログイン確認が必要');
      return;
    }
    const nextJobGuid = Global.createClientUuid();
    setIsSending(true);
    setLastEventSeq(0);
    setAgentMessages([]);
    setActiveJobGuid(nextJobGuid);
    setActivityText('送信中');
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('user_domain', employeeDomain);
      fd.append('ip_address', window.location.hostname);
      fd.append('user_agent', navigator.userAgent);
      fd.append('prompt', prompt);
      fd.append('agent_guid', activeAgentGUID);
      fd.append('job_guid', nextJobGuid);
      if (agentViewContext) {
        fd.append('context_json', JSON.stringify(agentViewContext));
      }
      if (selectedFile) {
        fd.append('file', selectedFile);
      }
      const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agent/push`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPromptText('');
      setSelectedFile(null);
      setActivityText(res.data?.job_guid ? `完了: ${res.data.job_guid}` : '完了');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setActivityText(e?.response?.data?.detail || '送信失敗');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!activeJobGuid) return;
    let stopped = false;
    const poll = async () => {
      const token = Global.getCurrentAuthToken(authToken);
      if (!token || stopped) return;
      try {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('user_domain', employeeDomain);
        fd.append('ip_address', window.location.hostname);
        fd.append('user_agent', navigator.userAgent);
        fd.append('job_guid', activeJobGuid);
        fd.append('after_seq', String(lastEventSeq));
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agent/events`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const events: AgentEvent[] = Array.isArray(res.data?.events) ? res.data.events : [];
        if (events.length > 0) {
          setLastEventSeq(events[events.length - 1].seq ?? lastEventSeq);
          const visibleMessages = events
            .filter((event) => event.display_flag !== 0 && event.message)
            .map((event) => `${event.role || event.status || 'agent'}: ${event.message}`);
          if (visibleMessages.length > 0) {
            setAgentMessages([visibleMessages[visibleMessages.length - 1]]);
          }
        }
        const jobStatus = res.data?.job?.status;
        if (jobStatus === 'FINISHED' || jobStatus === 'ERROR' || jobStatus === 'CANCELED') {
          setActivityText(jobStatus);
          setActiveJobGuid('');
        }
      } catch (e: any) {
        if (e?.response?.status === 404) return;
        setActivityText(e?.response?.data?.detail || '状態取得失敗');
      }
    };
    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 1000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [activeJobGuid, authToken, employeeDomain, lastEventSeq]);

  useEffect(() => {
    if (!activityRef.current) return;
    activityRef.current.scrollTop = activityRef.current.scrollHeight;
  }, [activityText, agentMessages, viewContextLabel]);

  return (
    <section className="aiagent-widget" aria-label="AIエージェント">
      <div className="aiagent-header">
        <span className="aiagent-badge">AGENT</span>
        <span className="aiagent-mood" aria-hidden="true">{moodIcon}</span>
        <select
          className="aiagent-select"
          value={activeAgentGUID}
          aria-label="エージェント選択"
          onChange={(e) => changeActiveAgent(e.target.value)}
        >
          <option value="">未選択</option>
          {agents.map((agent) => (
            <option key={agent.エージェントGUID} value={agent.エージェントGUID}>
              {agent.ニックネーム || agent.エージェントGUID}
            </option>
          ))}
        </select>
      </div>
      <div ref={activityRef} className="aiagent-activity" aria-label="アクティビティ">
        <div>{activityText}</div>
        {agentMessages.map((message, index) => (
          <p key={`${index}-${message}`}>{message}</p>
        ))}
        {viewContextLabel && <small>{viewContextLabel}</small>}
      </div>
      <div className="aiagent-tools" aria-label="エージェント操作">
        <button
          type="button"
          className={micEnabled ? 'aiagent-tool active' : 'aiagent-tool'}
          title="マイク"
          aria-pressed={micEnabled}
          onClick={() => setMicEnabled((v) => !v)}
        >
          <i className="fa-solid fa-microphone" />
        </button>
        <button
          type="button"
          className={speakerEnabled ? 'aiagent-tool active' : 'aiagent-tool'}
          title="スピーカー"
          aria-pressed={speakerEnabled}
          onClick={() => setSpeakerEnabled((v) => !v)}
        >
          <i className="fa-solid fa-volume-high" />
        </button>
        <button
          type="button"
          className={cameraEnabled ? 'aiagent-tool active' : 'aiagent-tool'}
          title="カメラ"
          aria-pressed={cameraEnabled}
          onClick={() => setCameraEnabled((v) => !v)}
        >
          <i className="fa-solid fa-camera" />
        </button>
        <button type="button" className="aiagent-tool" title="承認">
          <i className="fa-solid fa-check-to-slot" />
        </button>
        <button
          type="button"
          className={selectedFile ? 'aiagent-tool active' : 'aiagent-tool'}
          title="添付"
          onClick={() => fileInputRef.current?.click()}
        >
          <i className="fa-solid fa-paperclip" />
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="aiagent-file-input"
        onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
      />
      <textarea
        className="aiagent-input"
        rows={5}
        placeholder="ソフィアへ依頼..."
        value={promptText}
        onChange={(e) => setPromptText(e.target.value)}
      />
      <button
        type="button"
        className="aiagent-send"
        title="送信"
        disabled={!promptText.trim() || isSending}
        onClick={sendPrompt}
      >
        <i className="fa-solid fa-paper-plane" />
      </button>
    </section>
  );
};

export default AiAgent;
